import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { AccountRoutePaths } from "../../routes";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import colors from "../../../shared/themes/colors";
import MachineLogoIcon from "../../../shared/assets/icons/MachineLogoIcon";
import Loader from "../../../shared/components/Loader";
import useTranslation from "../../../shared/hooks/useTranslation";
import { refreshAccount } from "../../accountSlice";
import { resetLoginState } from "../../loginSlice";
import LoginForm from "./LoginForm";
import { isComAppHost } from "../../../shared/utils/hostPolicy";
import useDefaultRedirect from "../../hooks/useDefaultRedirect";

const LoginPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { defaultRoute } = useDefaultRedirect();

  const { isLoading } = useAppSelector((state) => state.login);
  const { user } = useAppSelector((state) => state.account);

  const location = useLocation();

  const { translate } = useTranslation("account");

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleLoginSuccess = () => {
    dispatch(refreshAccount(null));
    const search = new URLSearchParams(location.search);
    const returnUrl = search.get("returnUrl");
    const targetUrl = returnUrl || defaultRoute();
    navigate(targetUrl, { replace: true });
  };

  const handleForgotPassword = () => {
    navigate(AccountRoutePaths.forgotPassword);
  };

  const handleToRegistration = () => {
    navigate(AccountRoutePaths.signUp);
  };

  useEffect(() => {
    return () => {
      dispatch(resetLoginState());
    };
  }, [dispatch]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        paddingY: isMobile ? "32px" : "64px",
        backgroundColor: colors.secondary.gray98,
      }}
    >
      <Card
        sx={{
          maxWidth: "600px",
          width: "100%",
          margin: "0 16px",
          display: "flex",
          flexDirection: "column",
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          padding: isMobile ? "32px 16px 32px" : "64px 48px 64px",
        }}
      >
        {(isLoading || user) ? (
          <Box
            sx={{
              width: '100%',
              minHeight: '420px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Loader />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <MachineLogoIcon />
              <Box
                sx={{
                  fontFamily: "Work Sans",
                  fontSize: isMobile ? "24px" : "36px",
                  fontWeight: "500",
                  lineHeight: isMobile ? "32px" : "42px",
                  letterSpacing: "-0.072px",
                }}
              >
                {translate("logIn")}
              </Box>
            </Box>

            <CardContent sx={{ pt: 4 }}>
              {/* HOST_POLICY_SPEC §6 FE-B: no public self-registration on the `.com` app hosts —
                  hide the "Not a member? Sign up" affordance there. `.cc` is unchanged. */}
              <LoginForm
                formId="loginPageForm"
                onSubmitSuccess={handleLoginSuccess}
                onForgotPassword={handleForgotPassword}
                onRegistration={handleToRegistration}
                showRegistration={!isComAppHost()}
              />
            </CardContent>
          </>
        )}
      </Card>
    </Box>
  );
};

export default LoginPage;